import { DefaultButton, DetailsList, IColumn, Icon, Link, MessageBar, SelectionMode, Spinner, Stack } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { EntityAPI, EntityType, IEntityColumn, IEntityToken, ITriggerAction, IUserConfigPackage, TriggerActionType } from '../../../API';
import TriggerActionEditor, { TriggerActionEditorState } from './TriggerActionEditor';

export class TriggerActionsState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public listUrl: string = '';
	
	update: (ids: string[]) => void;
	
	items: ITriggerAction[] = [];
	editState: TriggerActionEditorState;

	constructor(userConfig: IUserConfigPackage,  update:(ids:string[]) => void, listUrl: string = '') {
		this.userConfig = userConfig;
		this.update = update;
		this.listUrl = listUrl;
		
		this.editState = new TriggerActionEditorState(this.userConfig, this.delete.bind(this), this.save.bind(this), this.listUrl);

		makeAutoObservable(this);
	}

	public setTokens(tokens:IEntityToken[]) {
		this.editState.setTokens(tokens);
	}

	public setListUrl(listUrl: string) {
		this.listUrl = listUrl;
		this.editState.setListUrl(listUrl);
	}
	
	public setAvailableFields(cols:IEntityColumn[]) {
		this.editState.setAvailableFields(cols);
	}

	public async setSelected(ids:string[]) {
		this.loading = true;

		if(ids.length > 0) {
			let api = await this.getApi();
			let profiles = await api.items(ids, null);
			this.setItems(profiles);
		} else {
			this.setItems([]);
		}
	}

	private async getApi() : Promise<EntityAPI<ITriggerAction>> {
		return await EntityAPI.fromEntityType(
			EntityType.TriggerAction,
			this.userConfig,
		) as EntityAPI<ITriggerAction>;
	}

	setItems(items: ITriggerAction[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}

	edit(item: ITriggerAction) {
		if(this.editState) {
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
		}
	}
	
	public showAddNewDialog() {
		this.edit({
			ActionType: TriggerActionType.SetField,
			ActionArgs: '',
			Id: '',
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: ''
		});
	}

	public async save(original:ITriggerAction, updated:ITriggerAction) : Promise<void> {

		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:ITriggerAction) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(profile:ITriggerAction) {
		this.items = this.items.filter(p => p !== profile);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		this.update(this.items.map(p => p.Id));
	}
}

function TriggerActions({ state }: { state: TriggerActionsState }) {


	let columns: IColumn[] = [
		{
			key: 'resource',
			name: 'Template Resource',
			minWidth: 250,
			maxWidth: 250,
			onRender: (item: ITriggerAction) => <Link onClick={() => {
				state.edit(item);
			}}>{item.ActionType === TriggerActionType.SetField ? 'Set Field' : 'Send Email'}</Link>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 60,
			maxWidth: 60,
			onRender: (item: ITriggerAction) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}
	
	return (
		<Stack>
			<Stack horizontal>
				<>
					{state.editState && <TriggerActionEditor state={state.editState} /> }

					{state.items.length === 0 ?
						<MessageBar>No actions have been configured. Click 'Add action' to add a new action.</MessageBar>
						:
						<></>
					}
					
					{(state?.items && state.items.length) ?
						<DetailsList isHeaderVisible={false} selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
						<></>
					}
				</>

			</Stack>
			<Stack horizontal>
				<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add action</DefaultButton>
			</Stack>
		</Stack>
	);
}

export default observer(TriggerActions);
